/**
 * Mock recording hook for testing Public Recording UI
 * Triggered by ?mock=true URL parameter
 *
 * Simulates all recording states without API calls or actual audio recording.
 * Generates synthetic audio levels for waveform visualization.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface MockRecordingReturn {
  // Init state
  isValidated: boolean;
  isValidating: boolean;
  scribeJobId: string;
  gatewayToken: string;
  error: string | null;
  // Recording state
  isRecording: boolean;
  isPaused: boolean;
  isUploading: boolean;
  isProcessing: boolean;
  // Audio
  audioLevel: number;
  // Handlers
  handleStartRecording: () => void;
  handlePauseRecording: () => void;
  handleResumeRecording: () => void;
  handleStopRecording: () => void;
}

export const useMockRecording = (): MockRecordingReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  const audioIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Synthetic audio level generator
  useEffect(() => {
    if (isRecording && !isPaused) {
      audioIntervalRef.current = setInterval(() => {
        const sine = Math.abs(Math.sin(Date.now() / 300));
        const noise = Math.random();
        setAudioLevel(sine * 0.5 + noise * 0.5);
      }, 30);
    } else {
      setAudioLevel(0);
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
        audioIntervalRef.current = null;
      }
    }

    return () => {
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
      }
    };
  }, [isRecording, isPaused]);

  const handleStartRecording = useCallback(() => {
    setIsRecording(true);
    setIsPaused(false);
  }, []);

  const handlePauseRecording = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleResumeRecording = useCallback(() => {
    setIsPaused(false);
  }, []);

  const handleStopRecording = useCallback(() => {
    setIsRecording(false);
    setIsPaused(false);
    setIsUploading(true);

    // Simulate upload -> processing -> done
    setTimeout(() => {
      setIsUploading(false);
      setIsProcessing(true);
      setTimeout(() => {
        setIsProcessing(false);
      }, 2000);
    }, 2000);
  }, []);

  return {
    isValidated: true,
    isValidating: false,
    scribeJobId: 'mock-job',
    gatewayToken: 'mock-token',
    error: null,
    isRecording,
    isPaused,
    isUploading,
    isProcessing,
    audioLevel,
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
  };
};
